import {
  MigrationManifest,
  PersistState,
  createMigrate,
  persistReducer
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import {
  SetSiteFcAction,
  SetSitePortIdAction,
  SetThisWorkstationAction
} from "~/redux/actions/workstations";
import {
  WorkstationPortSummaryDto,
  WorkstationSummaryDto,
  TotePlacementSummaryDto
} from "~/types/api";

export type WorkstationModePath =
  | "induction"
  | "picking"
  | "maintenance"
  | "problem-solving"
  | "cycle-count"
  | "standby";

export type WorkstationRepresentationProps = {
  size: number;
  rows: number[];
  columns: number[];
  operatorCoordinate: {
    x: number;
    y: number;
  };
  portCoordinates: WorkstationPortSummaryDto[];
  totePlacements: TotePlacementSummaryDto[];
  onClickCb: (args: { x: number; y: number }) => void;
};

export type WorkstationsState = {
  siteWorkstation: WorkstationSummaryDto | null;
  siteAllPortIds: number[];
  siteFulfillmentCenterId: Guid | null;
  siteFcAbbreviation: string | null;
  sitePortId: number | null;
};

export const initialState: WorkstationsState = {
  siteWorkstation: null,
  siteAllPortIds: [],
  siteFulfillmentCenterId: null,
  siteFcAbbreviation: null,
  sitePortId: null
};

type Actions = SetThisWorkstationAction | SetSiteFcAction | SetSitePortIdAction;

export const workstationsReducer = (
  state: WorkstationsState | undefined,
  action: Actions
): WorkstationsState => {
  if (state === undefined) {
    return initialState;
  }

  switch (action.type) {
    case "workstations/SET_THIS_WORKSTATION": {
      const ports = [...(action.payload?.ports || [])];

      const parentPortId = ports.find((p) => p.parentPortId)?.parentPortId;

      const siteAllPortIds = [
        ...ports.map((p) => p.portId),
        ...(parentPortId ? [parentPortId] : [])
      ];

      const siteWorkstation = action.payload
        ? { ...action.payload, ports }
        : null;

      return {
        ...state,
        siteWorkstation,
        siteAllPortIds
      };
    }
    case "workstations/SET_SITE_FC":
      return {
        ...state,
        siteFulfillmentCenterId: action.payload.fcId,
        siteFcAbbreviation:
          action.payload.fcId === null
            ? null
            : (action.payload.abbreviation ?? state.siteFcAbbreviation)
      };
    case "workstations/SET_SITE_PORT_ID":
      return {
        ...state,
        sitePortId: action.payload
      };
    default:
      return state;
  }
};

type PersistWorkstationsStateV1 = Pick<
  WorkstationsState,
  | "siteWorkstation"
  | "siteAllPortIds"
  | "siteFulfillmentCenterId"
  | "sitePortId"
> & {
  _persist: PersistState;
};

type PersistWorkstationsStateV0 = Omit<
  PersistWorkstationsStateV1,
  | "siteWorkstation"
  | "siteAllPortIds"
  | "siteFulfillmentCenterId"
  | "sitePortId"
> & {
  thisWorkstation: WorkstationsState["siteWorkstation"];
  thisWorkstationAllPortIds: WorkstationsState["siteAllPortIds"];
  thisFcId: WorkstationsState["siteFulfillmentCenterId"];
  selectedPortId: WorkstationsState["sitePortId"];
};

const migrations: MigrationManifest = {
  1: (state): PersistWorkstationsStateV1 | undefined => {
    if (!state) {
      return state;
    }
    const {
      thisWorkstation,
      thisWorkstationAllPortIds,
      thisFcId,
      selectedPortId,
      ...otherState
    } = state as PersistWorkstationsStateV0;
    return {
      ...otherState,
      siteWorkstation: thisWorkstation,
      siteAllPortIds: thisWorkstationAllPortIds,
      siteFulfillmentCenterId: thisFcId,
      sitePortId: selectedPortId
    };
  }
};

const workstationsPersistConfig = {
  key: "workstations",
  version: 2,
  storage,
  debug: false,
  whitelist: [
    "siteWorkstation",
    "siteFulfillmentCenterId",
    "siteFcAbbreviation",
    "sitePortId",
    "siteAllPortIds"
  ],
  migrate: createMigrate(migrations, { debug: false })
};

export const workstations = persistReducer(
  workstationsPersistConfig,
  workstationsReducer
);
